import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createVideo, deleteVideo, getAllVideos, importFullVideoList, updateLivestream, updateVideo } from "src/api/videos";
import type { Video } from "src/components/videos/types";

export const useVideos = () => {
	const queryClient = useQueryClient();

	const {
		data: videosData,
		isLoading,
		error,
	} = useQuery({
		queryKey: ["videos"],
		queryFn: getAllVideos,
	});

	const createMutation = useMutation({
		mutationFn: (video: Omit<Video, "video_id">) => createVideo(video),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["videos"] });
		},
	});

	const updateLivestreamMutation = useMutation({
		mutationFn: ({ videoId, video }: { videoId: number; video: Partial<Video> }) => updateLivestream(videoId, video),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["videos"] });
		},
	});

	const updateMutation = useMutation({
		mutationFn: ({ videoId, video }: { videoId: number; video: Partial<Video> }) => updateVideo(videoId, video),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["videos"] });
		},
	});

	const deleteMutation = useMutation({
		mutationFn: (videoId: number) => deleteVideo(videoId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["videos"] });
		},
	});

	const importMutation = useMutation({
		mutationFn: (data: { livestream: Video; videos: Video[] }) => importFullVideoList(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["videos"] });
		},
	});

	return {
		videosData,
		isLoading,
		error,
		createVideo: createMutation.mutate,
		updateVideo: updateMutation.mutate,
		updateLivestream: updateLivestreamMutation.mutate,
		deleteVideo: deleteMutation.mutate,
		importVideos: importMutation.mutate,
		isCreating: createMutation.isPending,
		isUpdating: updateMutation.isPending,
		isDeleting: deleteMutation.isPending,
		isImporting: importMutation.isPending,
	};
};
